import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Chip, Stack, Typography } from '@mui/material';
import { Extension as ExtensionIcon } from '@mui/icons-material';
import { useAuthStore } from '../../store/authStore';
import {
  connectExtension,
  getExtensionStatus,
  ExtensionStatus,
  EXTENSION_STATUS_EVENT,
} from '../../services/extensionBridge';
import { errorMessage } from '../../utils/errors';

/**
 * Shows whether the browser extension is signed in to this account and lets
 * the user connect it with one click. Signing in to the dashboard connects it
 * automatically unless the user signed out from the extension itself.
 */
const ExtensionStatusCard: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const [status, setStatus] = useState<ExtensionStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setStatus(await getExtensionStatus());
  }, []);

  useEffect(() => {
    refresh();
    const onStatus = (event: Event) => setStatus((event as CustomEvent<ExtensionStatus>).detail);
    window.addEventListener(EXTENSION_STATUS_EVENT, onStatus);
    return () => window.removeEventListener(EXTENSION_STATUS_EVENT, onStatus);
  }, [refresh]);

  const connect = async () => {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      const next = await connectExtension(user.id, true);
      setStatus(next);
      if (!next.authenticated) setError('The extension did not accept the sign-in. Try again.');
    } catch (e) {
      setError(errorMessage(e, 'Could not connect the extension'));
    } finally {
      setBusy(false);
    }
  };

  if (!status || !user) return null;

  if (!status.installed) {
    return (
      <Alert
        severity="info"
        icon={<ExtensionIcon fontSize="inherit" />}
        sx={{ mb: 3 }}
        data-testid="extension-status"
        action={
          <Button color="inherit" size="small" onClick={refresh}>
            Check again
          </Button>
        }
      >
        The JobFlo extension was not detected in this browser. Once it is installed it signs in with this
        account automatically.
      </Alert>
    );
  }

  const connected = status.authenticated && status.userId === user.id;
  if (connected) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }} data-testid="extension-status">
        <Chip icon={<ExtensionIcon />} color="success" variant="outlined" size="small" label="Extension connected" />
        <Typography variant="body2" color="text.secondary">
          Signed in as {status.email}
          {status.version ? ` · v${status.version}` : ''}
        </Typography>
      </Stack>
    );
  }

  const detail = status.authenticated
    ? `The extension is signed in as ${status.email}.`
    : status.optedOut
      ? 'You signed out of the extension.'
      : 'The extension is not signed in.';

  return (
    <Alert
      severity={error ? 'error' : 'warning'}
      icon={<ExtensionIcon fontSize="inherit" />}
      sx={{ mb: 3 }}
      data-testid="extension-status"
      action={
        <Button color="inherit" size="small" onClick={connect} disabled={busy}>
          {busy ? 'Connecting…' : 'Connect extension'}
        </Button>
      }
    >
      {error || `${detail} Connect it to fill and track applications with this account.`}
    </Alert>
  );
};

export default ExtensionStatusCard;
